import {
  rowMatchesSalesImportBranch,
  salesImportBranchIdFromRow,
  sortSalesImportBranchIds,
} from "../config/salesImportBranchIds";
import {
  lineBillAmount,
  advancedReportLineAmount,
  loadLineChunkRowsForDateRange,
  normalizeVoucherType,
} from "./salesLineChunksReader";
import {
  normalizeTranDateString,
  parseTranDateLoose,
  rowMatchesDateRange,
  rowTranDateValue,
  toYmd,
} from "../utils/dateParse";

function toYm(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function toIsoWeek(d) {
  const tmp = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const day = tmp.getUTCDay() || 7;
  tmp.setUTCDate(tmp.getUTCDate() + 4 - day);
  const yearStart = new Date(Date.UTC(tmp.getUTCFullYear(), 0, 1));
  const weekNo = Math.ceil(((tmp - yearStart) / 86400000 + 1) / 7);
  return `${tmp.getUTCFullYear()}-W${String(weekNo).padStart(2, "0")}`;
}

function bucketKey(dateObj, granularity) {
  if (granularity === "monthly") return toYm(dateObj);
  if (granularity === "weekly") return toIsoWeek(dateObj);
  return normalizeTranDateString(dateObj) || toYmd(dateObj);
}

function pushTop(map, key, amount, qty) {
  if (!key) return;
  const prev = map.get(key) || { key, amount: 0, records: 0, qty: 0 };
  prev.amount += amount;
  prev.records += 1;
  prev.qty += qty;
  map.set(key, prev);
}

function sortDescByAmount(arr) {
  return [...arr].sort((a, b) => b.amount - a.amount);
}

function voucherTypeNoKey(row) {
  const vType = normalizeVoucherType(row);
  const voucherNo = String(row.voucherNo ?? row.VoucherNo ?? "").trim() || "—";
  return `${vType}::${voucherNo}`;
}

/** One BillAmount per Voucher Type + Voucher No (item lines deduped). */
function upsertUniqueVoucherBill(map, row) {
  const key = voucherTypeNoKey(row);
  const bill = lineBillAmount(row);
  const qty = Number(row.qty) || 0;
  const dt = parseTranDateLoose(rowTranDateValue(row));
  const vType = normalizeVoucherType(row);
  const prev = map.get(key);
  if (!prev) {
    map.set(key, { voucherType: vType, billAmount: bill, qty, tranDate: dt });
    return;
  }
  prev.qty += qty;
  if (bill > prev.billAmount) prev.billAmount = bill;
  if (!prev.tranDate && dt) prev.tranDate = dt;
}

function buildTrendAndVoucherBreakdown(uniqueVouchers, { granularity, voucherType }) {
  const trend = new Map();
  const voucher = new Map();
  let uniqueVoucherCount = 0;

  for (const entry of uniqueVouchers.values()) {
    if (voucherType !== "all" && voucherType !== entry.voucherType) continue;
    uniqueVoucherCount += 1;
    const amount = entry.billAmount;
    const qty = entry.qty;
    pushTop(voucher, entry.voucherType, amount, qty);

    if (!entry.tranDate) {
      const t = trend.get("NO_DATE") || { key: "NO_DATE", amount: 0, records: 0, qty: 0 };
      t.amount += amount;
      t.records += 1;
      t.qty += qty;
      trend.set("NO_DATE", t);
    } else {
      const bucket = bucketKey(entry.tranDate, granularity);
      const t = trend.get(bucket) || { key: bucket, amount: 0, records: 0, qty: 0 };
      t.amount += amount;
      t.records += 1;
      t.qty += qty;
      trend.set(bucket, t);
    }
  }

  return {
    trend: [...trend.values()].sort((a, b) => a.key.localeCompare(b.key)),
    voucherBreakdown: sortDescByAmount([...voucher.values()]),
    uniqueVoucherCount,
  };
}

export function rowBranchId(row) {
  return salesImportBranchIdFromRow(row);
}

function rowMatchesBranch(row, branchId) {
  return rowMatchesSalesImportBranch(row, branchId);
}

/** Unique BranchID values from rows (sorted). */
export function collectBranchOptions(rows) {
  const set = new Set();
  for (const row of rows || []) {
    set.add(rowBranchId(row));
  }
  return sortSalesImportBranchIds([...set]);
}

/**
 * Build advanced report from in-memory rows (full uploaded week — same as View Records).
 */
export function generateAdvancedSalesReportFromRows(
  rows,
  {
    fromDate = "",
    toDate = "",
    granularity = "daily",
    voucherType = "all",
    branchId = "all",
    topN = 10,
    highValueThreshold = 50000,
    discountPctThreshold = 40,
    sourceLabel = "",
  } = {}
) {
  const from = fromDate ? new Date(`${fromDate}T00:00:00`) : null;
  const to = toDate ? new Date(`${toDate}T23:59:59.999`) : null;
  const hasDateFilter = Boolean(fromDate || toDate);
  const daySpan = from && to ? Math.max(1, Math.ceil((to - from) / 86400000) + 1) : 30;
  const prevTo = from ? new Date(from.getTime() - 1) : null;
  const prevFrom = prevTo ? new Date(prevTo.getTime() - (daySpan - 1) * 86400000) : null;

  const uniqueVouchers = new Map();
  const customer = new Map();
  const item = new Map();
  const employee = new Map();
  const seenBillProduct = new Set();
  const voucherTypes = new Set();
  const branchIds = new Set();

  let scanned = 0;
  let matched = 0;
  let totalAmount = 0;
  let totalQty = 0;
  let totalDiscount = 0;
  let invalidDateCount = 0;
  let missingIdentity = 0;
  let duplicateInvoiceItem = 0;
  let highValueCount = 0;
  let highDiscountCount = 0;
  let negativeOrZeroAmount = 0;
  let zeroQtyCount = 0;
  let currentPeriodAmount = 0;
  let previousPeriodAmount = 0;
  let currentPeriodRecords = 0;
  let previousPeriodRecords = 0;

  for (const row of rows) {
    scanned += 1;
    branchIds.add(rowBranchId(row));
    if (hasDateFilter && !rowMatchesDateRange(row, fromDate, toDate)) continue;
    if (!rowMatchesBranch(row, branchId)) continue;

    const vType = normalizeVoucherType(row);
    voucherTypes.add(vType);
    if (voucherType !== "all" && voucherType !== vType) continue;

    matched += 1;
    const amount = advancedReportLineAmount(row);
    const qty = Number(row.qty) || 0;
    const discountAmt = Number(row.discountAmt) || 0;
    const amountBeforeDiscount = amount + Math.max(0, discountAmt);
    const discountPct = amountBeforeDiscount > 0 ? (discountAmt / amountBeforeDiscount) * 100 : 0;

    totalAmount += amount;
    totalQty += qty;
    totalDiscount += discountAmt;

    upsertUniqueVoucherBill(uniqueVouchers, row);

    const dt = parseTranDateLoose(rowTranDateValue(row));
    if (!dt) {
      invalidDateCount += 1;
    } else if (hasDateFilter && from && to && prevFrom && prevTo) {
      if (dt >= from && dt <= to) {
        currentPeriodAmount += amount;
        currentPeriodRecords += 1;
      } else if (dt >= prevFrom && dt <= prevTo) {
        previousPeriodAmount += amount;
        previousPeriodRecords += 1;
      }
    }

    pushTop(customer, String(row.customerName || "UNKNOWN"), amount, qty);
    pushTop(item, String(row.itemName || row.partNo || "UNKNOWN"), amount, qty);
    pushTop(employee, String(row.employeeName || "UNKNOWN"), amount, qty);

    const bill = String(row.billNo || "").trim();
    const product = String(row.productId || "").trim();
    if (!bill || !product) missingIdentity += 1;
    const keyBP = `${bill}_${product}`;
    if (seenBillProduct.has(keyBP)) duplicateInvoiceItem += 1;
    else if (bill && product) seenBillProduct.add(keyBP);

    if (amount <= 0) negativeOrZeroAmount += 1;
    if (qty === 0) zeroQtyCount += 1;
    if (Math.abs(amount) >= highValueThreshold) highValueCount += 1;
    if (discountPct >= discountPctThreshold) highDiscountCount += 1;
  }

  const growthAmountPct =
    previousPeriodAmount !== 0
      ? ((currentPeriodAmount - previousPeriodAmount) / Math.abs(previousPeriodAmount)) * 100
      : null;
  const growthRecordPct =
    previousPeriodRecords !== 0
      ? ((currentPeriodRecords - previousPeriodRecords) / Math.abs(previousPeriodRecords)) * 100
      : null;

  const { trend, voucherBreakdown, uniqueVoucherCount } = buildTrendAndVoucherBreakdown(
    uniqueVouchers,
    { granularity, voucherType }
  );

  return {
    meta: {
      scanned,
      matched,
      uniqueVoucherCount,
      voucherTrendAmountBasis: "billAmountPerVoucherTypeAndNo",
      fromDate: fromDate || "",
      toDate: toDate || "",
      granularity,
      voucherType,
      branchId: branchId === "all" ? "" : branchId,
      sourceLabel,
      hasDateFilter,
    },
    kpis: {
      totalAmount,
      totalQty,
      totalDiscount,
      totalRecords: matched,
      avgBillValue: matched ? totalAmount / matched : 0,
      avgDiscountPct:
        totalAmount + totalDiscount > 0 ? (totalDiscount / (totalAmount + totalDiscount)) * 100 : 0,
    },
    trend,
    voucherBreakdown,
    topCustomers: sortDescByAmount([...customer.values()]).slice(0, topN),
    topItems: sortDescByAmount([...item.values()]).slice(0, topN),
    topEmployees: sortDescByAmount([...employee.values()]).slice(0, topN),
    comparison: {
      currentPeriodAmount: hasDateFilter ? currentPeriodAmount : totalAmount,
      previousPeriodAmount: hasDateFilter ? previousPeriodAmount : 0,
      currentPeriodRecords: hasDateFilter ? currentPeriodRecords : matched,
      previousPeriodRecords: hasDateFilter ? previousPeriodRecords : 0,
      growthAmountPct: hasDateFilter ? growthAmountPct : null,
      growthRecordPct: hasDateFilter ? growthRecordPct : null,
      periodDays: hasDateFilter ? daySpan : null,
    },
    anomalies: {
      highValueCount,
      highDiscountCount,
      negativeOrZeroAmount,
      zeroQtyCount,
    },
    audit: {
      invalidDateCount,
      missingIdentity,
      duplicateInvoiceItem,
    },
    voucherTypeOptions: [...voucherTypes].sort((a, b) => a.localeCompare(b)),
    branchOptions: sortSalesImportBranchIds([...branchIds]),
  };
}

/**
 * Advanced reporting from weekly lineChunks.
 * Prefer generateAdvancedSalesReportFromRows with loadWeeklyLineChunkRows for one upload week.
 */
export async function generateAdvancedSalesReport({
  fromDate,
  toDate,
  granularity = "daily",
  voucherType = "all",
  branchId = "all",
  topN = 10,
  highValueThreshold = 50000,
  discountPctThreshold = 40,
  onProgress,
  rows: preloadedRows = null,
  sourceLabel = "",
}) {
  let rows = preloadedRows;
  if (!rows) {
    if (!fromDate && !toDate) {
      throw new Error("Set From/To dates, or select an uploaded week.");
    }
    const loaded = await loadLineChunkRowsForDateRange({ fromDate, toDate }, (rowCount) => {
      onProgress?.(rowCount);
    });
    rows = loaded.rows;
    sourceLabel =
      sourceLabel ||
      `${rows.length.toLocaleString()} lines (TranDate ${fromDate || "…"} → ${toDate || "…"})`;
  } else {
    onProgress?.(rows.length);
  }

  return generateAdvancedSalesReportFromRows(rows, {
    fromDate,
    toDate,
    granularity,
    voucherType,
    branchId,
    topN,
    highValueThreshold,
    discountPctThreshold,
    sourceLabel,
  });
}
